import { MetricValueMode, MultiPlayerAggregate } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithQuery, atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { metricDistributionParamsAtom } from '@/atoms/dataLocker/metrics';
import { atomWithDebounce } from '@/atoms/debounce';
import { maxAgeBirthDateToQueryAtom, minAgeBirthDateToQueryAtom } from '@/atoms/filters/dataLocker/ageRange';
import { filterValueAtom } from '@/atoms/filters/filter';
import { competitionIdsToFilterByAtom, selectedCompetitionsAtom } from '@/atoms/filters/highLevel/competitions';
import { selectedPositionsFilterParamsAtom } from '@/atoms/filters/highLevel/positions';
import { seasonIdsToFilterByAtom } from '@/atoms/filters/highLevel/seasons';
import { unwrappedPlayerSelectedGamesForQueryAtom } from '@/atoms/filters/player/playerGames';
import { adjustedOffsetAtom, clientOrderParamsAtom, pageAndOrderParamsAtom } from '@/atoms/general';
import { fetchClientAtom } from '@/atoms/queries/client';
import { rawPlayersWithCacheFallbackAtom } from '@/atoms/queries/players';
import { metricValueModeAtom, visibleTableColumnsAtom } from '@/atoms/table';
import { createEnrichedDataAtom } from '@/atoms/utils/enrichment';
import { PREFETCH_ROW_COUNT } from '@/consts/pagination';
import { playersNormalisedAggregatesUrl, playersTotalAggregatesUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { convertFilterParamsToString, hasEmptyInFilters } from '@/utils/api';
import { limitArray, sortFetchResultObjectArray } from '@/utils/array';
import {
  countNumVisibleActiveFilters,
  createFilterParams,
  FilterDefinition,
  minMaxFilterAtom,
  objectFilterParamsFn,
  propertyFilterParamsFn,
} from '@/utils/atoms/filter';
import { getOnDemandIncludeParams } from '@/utils/metrics';
import { addLink, flattenAndAddPrefix, optionsToValueStrings } from '@/utils/object';
import { objSnakeToCamel } from '@/utils/queries';
import { unwrappedUserDefaultTeamCyclesOrderedAtom } from '../teams';
import type { FilterParams } from '@statsbomb/parachute-types';

const playerAggsAgeFilterParamsAtom = minMaxFilterAtom(
  'output.player.date_of_birth',
  minAgeBirthDateToQueryAtom,
  maxAgeBirthDateToQueryAtom,
);

const playerAggsHeightFilterParamsAtom = minMaxFilterAtom(
  'output.player.height',
  filterValueAtom('minHeight'),
  filterValueAtom('maxHeight'),
);

const playerAggsMinutesFilterParamsAtom = minMaxFilterAtom(
  'output.aggregates.minutes_played',
  filterValueAtom('minMinutes'),
  filterValueAtom('maxMinutes'),
);

// NOTE: I don't understand why, but I can't define this as a const object. If I do that, the tests fail because the
// atom competitionIdsToFilterByAtom is undefined. If I define this as a function that returns the object, they pass,
// so that's what I've had to do ¯\_(ツ)_/¯
/** This is the definition of the filters that are used in the player data locker. */
const playerDataLockerFilterDefinitions = (): {
  general: FilterDefinition[];
  player: FilterDefinition[];
  metric: FilterDefinition[];
} => ({
  general: [
    {
      apiFilterParams: objectFilterParamsFn(competitionIdsToFilterByAtom, 'input.competition_id'),
      visibleFilterParams: objectFilterParamsFn(selectedCompetitionsAtom, 'input.competition_id'),
      filterOperator: 'in',
    },
    {
      apiFilterParams: objectFilterParamsFn(seasonIdsToFilterByAtom, 'input.season_id'),
      filterOperator: 'in',
    },
    {
      apiFilterParams: objectFilterParamsFn(filterValueAtom('teamIds'), 'input.team_id'),
      filterOperator: 'in',
    },
    {
      apiFilterParams: objectFilterParamsFn(filterValueAtom('playerIds'), 'output.player.player_id'),
      filterOperator: 'in',
    },
    [
      {
        apiFilterParams: objectFilterParamsFn(filterValueAtom('startDate'), 'input.game_date'),
        filterOperator: 'gte',
      },
      {
        apiFilterParams: objectFilterParamsFn(filterValueAtom('endDate'), 'input.game_date'),
        filterOperator: 'lte',
      },
    ],
  ],
  player: [
    {
      apiFilterParams: objectFilterParamsFn(selectedPositionsFilterParamsAtom, 'input.position'),
      visibleFilterParams: objectFilterParamsFn(filterValueAtom('positions'), 'input.position'),
      filterOperator: 'in',
    },
    [
      {
        apiFilterParams: propertyFilterParamsFn(playerAggsAgeFilterParamsAtom, 'max'),
        filterOperator: 'gte',
      },
      {
        apiFilterParams: propertyFilterParamsFn(playerAggsAgeFilterParamsAtom, 'min'),
        filterOperator: 'lte',
      },
    ],
    [
      {
        apiFilterParams: propertyFilterParamsFn(playerAggsHeightFilterParamsAtom, 'min'),
        filterOperator: 'gte',
      },
      {
        apiFilterParams: propertyFilterParamsFn(playerAggsHeightFilterParamsAtom, 'max'),
        filterOperator: 'lte',
      },
    ],
    [
      {
        apiFilterParams: propertyFilterParamsFn(playerAggsMinutesFilterParamsAtom, 'min'),
        filterOperator: 'gte',
      },
      {
        apiFilterParams: propertyFilterParamsFn(playerAggsMinutesFilterParamsAtom, 'max'),
        filterOperator: 'lte',
      },
    ],
    {
      apiFilterParams: objectFilterParamsFn(filterValueAtom('nationality'), 'output.player.nationality_area_code'),
      filterOperator: 'in',
    },
    {
      apiFilterParams: objectFilterParamsFn(filterValueAtom('preferredFoot'), 'output.player.preferred_foot'),
      filterOperator: 'in',
    },
  ],
  metric: [
    [
      {
        apiFilterParams: propertyFilterParamsFn(metricDistributionParamsAtom, 'lte'),
        filterOperator: 'lte',
      },
      {
        apiFilterParams: propertyFilterParamsFn(metricDistributionParamsAtom, 'gte'),
        filterOperator: 'gte',
      },
    ],
  ],
});

export const numGeneralFiltersActiveAtom = atom(get =>
  countNumVisibleActiveFilters(get, playerDataLockerFilterDefinitions().general),
);

export const numPlayerFiltersActiveAtom = atom(get =>
  countNumVisibleActiveFilters(get, playerDataLockerFilterDefinitions().player),
);

export const playerAggsFilterParamsAtom = atom(get =>
  createFilterParams(get, Object.values(playerDataLockerFilterDefinitions()).flat()),
);

export const playerGameStatsFilterParamsAtom = atom(get => ({
  in: {
    'input.game_id': optionsToValueStrings(get(unwrappedPlayerSelectedGamesForQueryAtom)),
  },
}));

export const playerAggsFilterParamsDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
export const { debouncedValueAtom: debouncedPlayerAggsFilterParamsAtom } = playerAggsFilterParamsDebounceObject;

/* 
https://tkdodo.eu/blog/effective-react-query-keys#automatic-refetching
This is needed as we only want to update the filters on press from the "Apply" button.
We use this atom to store the filters that are selected once the apply button is clicked
and then use those filters as we would to query the data.
*/
export const playerAggsBaseFiltersAtom = atom<FilterParams | null>(null);

export const playerAggsShowApplyButtonAtom = atom(get => {
  const playerAggsBaseFilters = get(playerAggsBaseFiltersAtom);
  const playerAggsFilterParams = get(playerAggsFilterParamsAtom);

  const newQueryParams = convertFilterParamsToString(playerAggsFilterParams);
  const currentQueryParams = convertFilterParamsToString(playerAggsBaseFilters);

  /* on initial render when we haven't changed anything we don't want to do the comparison below */
  if (currentQueryParams === '') return false;

  return newQueryParams !== currentQueryParams;
});

export const hasPlayerAggsCompAndSeasonFilterParamsAtom = atom(get => {
  const playerAggsFilterParams = get(playerAggsFilterParamsAtom);
  const unwrappedUserDefaultTeamCyclesOrdered = get(unwrappedUserDefaultTeamCyclesOrderedAtom);

  if (!unwrappedUserDefaultTeamCyclesOrdered || unwrappedUserDefaultTeamCyclesOrdered.length === 0) return false;

  return (
    playerAggsFilterParams.in &&
    playerAggsFilterParams.in['input.competition_id'] !== undefined &&
    playerAggsFilterParams.in['input.season_id'] !== undefined
  );
});

const rawPlayerAggsDataAtom = atomWithQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const adjustedOffset = get(adjustedOffsetAtom);
  const { order_by: orderBy } = get(pageAndOrderParamsAtom);
  const playerAggsBaseFilters = get(playerAggsBaseFiltersAtom);
  const metricValueMode = get(metricValueModeAtom);
  const includeParams = getOnDemandIncludeParams({
    metricKeys: get(visibleTableColumnsAtom),
    orderBy,
    filters: playerAggsBaseFilters,
  });

  const queryKey = [
    'playerAggs',
    adjustedOffset,
    orderBy,
    playerAggsBaseFilters,
    metricValueMode,
    includeParams,
  ] as const;

  const queryFn = async ({
    queryKey: [, adjustedOffset, orderBy, playerAggsFilters, metricValueMode, includeParams],
  }: {
    queryKey: typeof queryKey;
  }) => {
    if (hasEmptyInFilters(playerAggsFilters)) return [];

    const urlCreationFunction =
      metricValueMode === MetricValueMode.Per90 ? playersNormalisedAggregatesUrl : playersTotalAggregatesUrl;

    const data: MultiPlayerAggregate[] = await fetch(
      urlCreationFunction({
        limit: PREFETCH_ROW_COUNT,
        offset: adjustedOffset,
        order_by: orderBy,
        ...(playerAggsFilters ?? {}),
        ...includeParams,
      }),
    );
    return data.map(item => flattenAndAddPrefix(objSnakeToCamel(item), '.', false)) as NestedObject[];
  };

  return {
    queryKey,
    queryFn,
    /* while still loading use the previous data, should work similarly to using
    "unwrap(atom)" with our suspensed atoms */
    placeholderData: (previousValue: NestedObject[] | undefined) => previousValue,
  };
});

const playerAggsDataWithLinksAtom = atom(async get => {
  const { offset, limit } = get(pageAndOrderParamsAtom);
  const { data, isLoading, isRefetching } = get(rawPlayerAggsDataAtom);

  if (!data) return { data, isLoading, isRefetching };

  const allPlayers = await get(rawPlayersWithCacheFallbackAtom);

  const dataWithCurrentTeam = data.map(row => {
    // As we only map through one page of data at a time, this is fast enough. In future, we could look to store the
    // big player list in an obj keyed by player_id.
    const fullPlayer = allPlayers.find(p => p.player_id === row['player.playerId']);
    if (!fullPlayer || !fullPlayer.current_team) return row;

    return {
      ...row,
      'currentTeam.team_id': fullPlayer.current_team.team_id,
      'currentTeam.name': fullPlayer.current_team.name,
    };
  });

  const sortedPlayerAggsToDisplay = sortFetchResultObjectArray(dataWithCurrentTeam, get(clientOrderParamsAtom));
  const playerAggsData = limitArray(sortedPlayerAggsToDisplay, { offset: offset % PREFETCH_ROW_COUNT, limit });

  const dataWithLinks = playerAggsData.map(data => {
    let result = addLink(data, data['player.displayName'] as string, data['player.playerId'] as number, 'playerLink');

    if (data['currentTeam.team_id']) {
      result = addLink(
        result,
        data['currentTeam.name'] as string,
        data['currentTeam.team_id'] as number,
        'currentTeam',
      );
    }

    return result;
  });

  return {
    data: dataWithLinks,
    isLoading,
    isRefetching,
  };
});

const unwrappedPlayerAggsDataWithLinksAtom = unwrap(
  playerAggsDataWithLinksAtom,
  prev => prev || { data: undefined, isLoading: false, isRefetching: false },
);

const enrichedPlayerAggsDataAtom = createEnrichedDataAtom(
  atom(get => get(unwrappedPlayerAggsDataWithLinksAtom).data),
  'player.playerId',
);

export const playerAggsDataAtom = atom(get => {
  const { isLoading, isRefetching } = get(unwrappedPlayerAggsDataWithLinksAtom);
  const enrichedData = get(enrichedPlayerAggsDataAtom);

  return {
    data: enrichedData,
    isLoading,
    isRefetching,
  };
});

export const rawPlayerAggsCountAtom = atomWithQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['playerAggsCount', get(playerAggsBaseFiltersAtom)] as const;
  const queryFn = async ({ queryKey: [, playerAggsFilterParams] }: { queryKey: typeof queryKey }) => {
    if (!playerAggsFilterParams) return null;

    return (await fetch(
      `/players/normalised-aggregates/count${convertFilterParamsToString({
        ...playerAggsFilterParams,
        ...getOnDemandIncludeParams({ filters: playerAggsFilterParams }),
      })}`,
    )) as Promise<number>;
  };
  return {
    queryKey,
    queryFn,
    /* while still loading use the previous data, should work similarly to using
    "unwrap(atom)" with our suspense atoms */
    placeholderData: (previousValue: number | undefined | null) => previousValue,
  };
});

export const playerAggsCountAtom = atom(get => get(rawPlayerAggsCountAtom).data);

export const hasRetrievedAllPlayerAggsAtom = atom(get => {
  const playerAggsCount = get(rawPlayerAggsCountAtom).data;
  const playerAggsDataCount = get(rawPlayerAggsDataAtom).data?.length;
  return (playerAggsCount && playerAggsDataCount && playerAggsCount === playerAggsDataCount) || false;
});

const rawPlayerAggsCountSuspenseAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['playerAggsCount', get(debouncedPlayerAggsFilterParamsAtom)] as const;
  const queryFn = async ({ queryKey: [, playerAggsFilterParams] }: { queryKey: typeof queryKey }) => {
    if (!playerAggsFilterParams) return null;

    return (await fetch(
      `/players/normalised-aggregates/count${convertFilterParamsToString({
        ...playerAggsFilterParams,
      })}`,
    )) as Promise<number>;
  };
  return { queryKey, queryFn };
});
export const playerAggsCountSuspenseAtom = atom(async get => (await get(rawPlayerAggsCountSuspenseAtom)).data);
